import { useState, useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import CMSController from "aldoo-ra/CMS/controllers/cms-controller"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import PlanValuesEditor from "aldoo-ra/CMSModels/ProductFeature/editors/plan-values-editor"
import Input from "aldoo-ra/Input"
import { Bars3Icon, TrashIcon, GlobeAltIcon, EyeSlashIcon } from "@heroicons/react/24/outline"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import LocalizationController from "aldoo-ra/Localization/localization-controller"
import LocalizationItemDialog from "aldoo-ra/Localization/localization-item-dialog"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import {
  pfvLocKey,
  loadPfvMap,
  hydrateFeatures,
  savePlanValues,
  deletePlanFeatureValue,
} from "./pfv-helpers"

const COLLAPSED_PREF_KEY = "featureMatrix.collapsedCategories"

const CELL_PRESET_OPTIONS = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
  { label: "Custom", value: "custom" },
]

function paramToPreset(param) {
  if (param === "✓") return [CELL_PRESET_OPTIONS[0]]
  if (param === "✗") return [CELL_PRESET_OPTIONS[1]]
  return [CELL_PRESET_OPTIONS[2]]
}

function InlineCellEditor({ entry: initialEntry, anchorRef, onApply, onClose, featureId, planId, pfvId }) {
  const initialParam = initialEntry?.param ?? ""
  const initialValue = initialEntry?.value ?? ""
  const [param, setParam] = useState(initialParam)
  const [sysValue, setSysValue] = useState(initialValue)
  const paramRef = useRef(initialParam)
  const sysValueRef = useRef(initialValue)
  const customDraft = useRef(initialParam !== "✓" && initialParam !== "✗" ? initialParam : "")
  const [customFields, setCustomFields] = useState(() => getCustomFields(initialEntry))
  const customFieldsRef = useRef(getCustomFields(initialEntry))
  const containerRef = useRef(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  // Localization
  const locController = LocalizationController()
  const [locOpen, setLocOpen] = useState(false)
  const [locItem, setLocItem] = useState(null)
  const [locLoading, setLocLoading] = useState(false)
  const [locLangsLoaded, setLocLangsLoaded] = useState(false)

  // Per-cell localization is keyed on the PlanFeatureValue _id. If the cell
  // doesn't yet have a persisted PFV (new entry), defer locale editing until
  // first save (caller passes pfvId once it exists).
  const locKey = pfvId ? pfvLocKey(pfvId) : null

  const openLocale = async (e) => {
    e.stopPropagation()
    if (!locKey) return
    setLocLoading(true)
    try {
      if (!locLangsLoaded) {
        const stats = await locController.getLanguageStatistics()
        if (stats?.languages) {
          locController.setLanguages(stats.languages)
          const primary = stats.languages.find((l) => l.primary)
          if (primary) locController.setPrimaryLanguage(primary.code)
          setLocLangsLoaded(true)
        }
      }
      let items = await locController.getLocalizationKey(locKey)
      if (!items || items.length === 0) {
        await locController.addLocalizationKey({
          key: locKey,
          type: "text",
          language: locController.primaryLanguage || "en",
          content: paramRef.current || "",
        })
        items = await locController.getLocalizationKey(locKey)
      }
      if (items?.[0]) {
        setLocItem(items[0])
        setLocOpen(true)
      }
    } finally {
      setLocLoading(false)
    }
  }

  useEffect(() => { paramRef.current = param }, [param])
  useEffect(() => { sysValueRef.current = sysValue }, [sysValue])

  useEffect(() => {
    const reposition = () => {
      if (!anchorRef?.current) return
      const rect = anchorRef.current.getBoundingClientRect()
      const editorHeight = containerRef.current?.offsetHeight || 90
      setPos({
        top: rect.top + window.scrollY - editorHeight - 6,
        left: rect.left + window.scrollX + rect.width / 2,
      })
    }
    reposition()
    window.addEventListener("scroll", reposition, true)
    window.addEventListener("resize", reposition)
    return () => {
      window.removeEventListener("scroll", reposition, true)
      window.removeEventListener("resize", reposition)
    }
  }, [anchorRef])

  const locOpenRef = useRef(locOpen)
  useEffect(() => { locOpenRef.current = locOpen }, [locOpen])

  useEffect(() => {
    const handleClick = (e) => {
      if (locOpenRef.current) return
      if (containerRef.current && !containerRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onApply(paramRef.current, sysValueRef.current, customFieldsRef.current)
      }
    }
    const t = setTimeout(() => document.addEventListener("mousedown", handleClick), 0)
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handleClick) }
  }, [onApply, anchorRef])

  const handleDropdown = (selected) => {
    const v = selected?.[0]?.value
    if (v === "yes") {
      if (paramRef.current !== "✓" && paramRef.current !== "✗") customDraft.current = paramRef.current
      onApply("✓", sysValueRef.current, customFieldsRef.current)
    } else if (v === "no") {
      if (paramRef.current !== "✓" && paramRef.current !== "✗") customDraft.current = paramRef.current
      onApply("✗", sysValueRef.current, customFieldsRef.current)
    } else if (v === "custom") {
      const restored = customDraft.current
      setParam(restored)
      paramRef.current = restored
    }
  }

  const updateCustomField = (index, field, val) => {
    const next = customFields.map((f, i) => i === index ? { ...f, [field]: val } : f)
    setCustomFields(next)
    customFieldsRef.current = next
  }

  const renameCustomField = (index, newKey) => {
    if (!newKey) return
    const next = customFields.map((f, i) => i === index ? { key: newKey, value: f.value } : f)
    setCustomFields(next)
    customFieldsRef.current = next
  }

  const removeCustomField = (index) => {
    const next = customFields.filter((_, i) => i !== index)
    setCustomFields(next)
    customFieldsRef.current = next
  }

  const addCustomField = () => {
    const existing = customFields.map((f) => f.key)
    let name = "field"
    let i = 1
    while (existing.includes(name)) { name = `field${i}`; i++ }
    const next = [...customFields, { key: name, value: "" }]
    setCustomFields(next)
    customFieldsRef.current = next
  }

  const portal = createPortal(
    <div
      ref={containerRef}
      style={{ position: "absolute", top: pos.top, left: pos.left, transform: "translateX(-50%)" }}
      className="z-[99999] bg-admin_paper border border-admin_border rounded-lg shadow-xl p-2 flex flex-col gap-2 min-w-[260px]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex gap-2 items-center">
        <DropDownSelect
          options={CELL_PRESET_OPTIONS}
          value={paramToPreset(param)}
          valueField="value"
          placeholder="Type"
          multiSelection={false}
          showCheckbox={false}
          showSelectAll={false}
          enableResetButton={false}
          fixedWidth={100}
          onChange={handleDropdown}
        />
        <input
          autoFocus
          value={param}
          onChange={(e) => { setParam(e.target.value); paramRef.current = e.target.value }}
          onKeyDown={(e) => { if (e.key === "Enter") onApply(paramRef.current, sysValueRef.current, customFieldsRef.current) }}
          className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-admin_border rounded focus:outline-none focus:ring-1 focus:ring-admin_primary bg-admin_paper text-admin_text"
          placeholder="Label"
        />
        {locKey && (
          <button
            onClick={openLocale}
            disabled={locLoading}
            className="p-1.5 text-admin_text_secondary hover:text-admin_primary transition disabled:opacity-50"
            title="Edit translations"
          >
            <GlobeAltIcon className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex gap-2 items-center">
        <span className="text-xs text-admin_text_secondary w-[100px] flex-shrink-0 text-right">value</span>
        <input
          value={sysValue}
          onChange={(e) => { setSysValue(e.target.value); sysValueRef.current = e.target.value }}
          onKeyDown={(e) => { if (e.key === "Enter") onApply(paramRef.current, sysValueRef.current, customFieldsRef.current) }}
          className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-admin_border rounded focus:outline-none focus:ring-1 focus:ring-admin_primary bg-admin_paper text-admin_text"
          placeholder="e.g. 5, unlimited"
        />
      </div>

      {customFields.map((f, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input
            defaultValue={f.key}
            onBlur={(e) => renameCustomField(i, e.target.value.trim())}
            className="w-[100px] flex-shrink-0 px-2 py-1.5 text-xs border border-admin_border rounded focus:outline-none focus:ring-1 focus:ring-admin_primary bg-admin_paper text-admin_text_secondary"
            placeholder="field name"
          />
          <input
            value={f.value ?? ""}
            onChange={(e) => updateCustomField(i, "value", e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onApply(paramRef.current, sysValueRef.current, customFieldsRef.current) }}
            className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-admin_border rounded focus:outline-none focus:ring-1 focus:ring-admin_primary bg-admin_paper text-admin_text"
            placeholder="value"
          />
          <button
            onClick={() => removeCustomField(i)}
            className="p-1.5 text-admin_text_secondary/40 hover:text-admin_error transition"
            title="Remove field"
          >
            <TrashIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}

      <button
        onClick={addCustomField}
        className="flex items-center gap-1 text-xs text-admin_primary hover:text-admin_primary/80 transition mt-0.5"
      >
        <span className="text-base leading-none">+</span> Add field
      </button>
    </div>,
    document.body
  )

  return (
    <>
      {portal}
      {locOpen && locItem && createPortal(
        <LocalizationItemDialog
          isOpen={locOpen}
          onClose={() => { setLocOpen(false); setLocItem(null) }}
          onSave={(updated) => setLocItem(updated)}
          item={locItem}
          controller={locController}
          parentObject={null}
          zIndex={100000}
        />,
        document.body
      )}
    </>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function resolvePlanId(plan) {
  return typeof plan === "string" ? plan : plan?._id
}

/**
 * Builds the parent ProductFeature payload only. `plan_values` is expected
 * to be an array of PlanFeatureValue ids (strings) at this point — the
 * actual PFV docs are persisted separately via savePlanValues().
 */
function normalizeFeatForSave(feat, id, planValueIds) {
  const catId = typeof feat.category === "string" ? feat.category : feat.category?._id

  // Strip any embedded plan_values localization entries (legacy shape).
  const existing = Array.isArray(feat.localization) ? feat.localization : []
  const localization = existing.filter((l) => !String(l.field).startsWith("plan_values"))

  // Auto-register `name` localization for existing items if missing
  if (id && !localization.some((l) => l.field === "name")) {
    localization.push({ field: "name", localizationKey: `ProductFeature-name-${id}` })
  }

  const { localization: _loc, plan_values: _pv, ...rest } = feat
  return {
    content: { ...rest, category: catId, plan_values: planValueIds },
    localization,
  }
}

function findPlanEntry(feature, plan) {
  return feature.plan_values?.find((pv) => {
    if (!pv.plan) return false
    const pvId = resolvePlanId(pv.plan)
    const pvSlug = pv.plan?.id || pv.plan?.content?.id
    return pvId === plan._id || pvSlug === plan.id
  })
}

function getValue(feature, plan) {
  return findPlanEntry(feature, plan)?.param || "—"
}

function getRawEntry(feature, plan) {
  const e = findPlanEntry(feature, plan)
  if (!e) return { param: "", value: "", params: {} }
  return e
}

// Custom fields live under entry.params (object). UI converts to [{key, value}].
function getCustomFields(entry) {
  const obj = entry?.params || {}
  return Object.keys(obj).map((k) => ({ key: k, value: obj[k] }))
}

function ValueBadge({ value }) {
  if (value === "✓") return <span className="text-green-500 font-bold text-base">✓</span>
  if (value === "✗" || value === "—") return <span className="text-admin_text_secondary/40">—</span>
  return <span className="text-sm text-admin_text">{value}</span>
}

async function syncPrimaryLocale(controller, locKey, value) {
  const lang = controller.primaryLanguage || "en"
  try {
    const items = await controller.getLocalizationKey(locKey)
    if (!items || items.length === 0) {
      await controller.addLocalizationKey({ key: locKey, type: "text", language: lang, content: value || "", silent: true })
    } else {
      await controller.saveLocalizationItem({ key: locKey, translations: { [lang]: { content: value || "" } } }, false)
    }
  } catch (e) {
    // silent fail — localization sync is best-effort
  }
}

// ─── Category Dialog ─────────────────────────────────────────────────────────

function CategoryDialog({ category, onSave, onClose }) {
  const [form, setForm] = useState({
    id: category?.id || "",
    name: category?.name || "",
    sort_order: category?.sort_order ?? 0,
    visible: category?.visible !== false,
  })

  const locController = LocalizationController()
  const [locOpen, setLocOpen] = useState(false)
  const [locItem, setLocItem] = useState(null)
  const [locLoading, setLocLoading] = useState(false)
  const [locLangsLoaded, setLocLangsLoaded] = useState(false)

  const openLocale = async () => {
    if (!category?._id) return
    const locKey = `FeatureCategory-name-${category._id}`
    setLocLoading(true)
    try {
      if (!locLangsLoaded) {
        const stats = await locController.getLanguageStatistics()
        if (stats?.languages) {
          locController.setLanguages(stats.languages)
          const primary = stats.languages.find((l) => l.primary)
          if (primary) locController.setPrimaryLanguage(primary.code)
          setLocLangsLoaded(true)
        }
      }
      let items = await locController.getLocalizationKey(locKey)
      if (!items || items.length === 0) {
        await locController.addLocalizationKey({
          key: locKey,
          type: "text",
          language: locController.primaryLanguage || "en",
          content: form.name || "",
        })
        items = await locController.getLocalizationKey(locKey)
      }
      if (items?.[0]) {
        setLocItem(items[0])
        setLocOpen(true)
      }
    } finally {
      setLocLoading(false)
    }
  }

  const handleSave = async () => {
    if (!form.id.trim() || !form.name.trim()) {
      showNotification({ message: "ID and Name are required", className: "bg-admin_error text-white" })
      return
    }
    if (category?._id) {
      await syncPrimaryLocale(locController, `FeatureCategory-name-${category._id}`, form.name)
    }
    onSave(form)
  }

  return (
    <Modal isOpen onClose={onClose} title={category?._id ? "Edit Category" : "Add Category"} variant="admin_compact_fit">
      <div className="p-4 space-y-3 w-[480px]">
        <div className="grid grid-cols-[1fr_80px] gap-3">
          <div>
            <label className="block text-xs font-medium text-admin_text_secondary mb-1">Name *</label>
            <div className="flex items-center gap-1">
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-2 py-1.5 border border-admin_border rounded text-sm focus:outline-none focus:ring-1 focus:ring-admin_primary"
                placeholder="Quotas & Limits"
                autoFocus
              />
              {category?._id && (
                <button
                  onClick={openLocale}
                  disabled={locLoading}
                  className="p-1.5 text-admin_text_secondary hover:text-admin_primary transition disabled:opacity-50 flex-shrink-0"
                  title="Edit translations"
                >
                  <GlobeAltIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-admin_text_secondary mb-1">Order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
              className="w-full px-2 py-1.5 border border-admin_border rounded text-sm focus:outline-none focus:ring-1 focus:ring-admin_primary"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-admin_text_secondary mb-1">ID *</label>
          <input
            value={form.id}
            onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
            className="w-full px-2 py-1.5 border border-admin_border rounded text-sm focus:outline-none focus:ring-1 focus:ring-admin_primary"
            placeholder="quotas"
          />
        </div>
        <div className="flex items-center justify-between py-1">
          <div>
            <label className="text-xs font-medium text-admin_text_secondary">Visible to users</label>
            <p className="text-[11px] text-admin_text_secondary/60 mt-0.5">When off, this group and all its features are hidden on the pricing page</p>
          </div>
          <ToggleSwitch
            isOn={form.visible}
            onChange={(v) => setForm((f) => ({ ...f, visible: v }))}
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>Save</Button>
        </div>
      </div>
      {locOpen && locItem && createPortal(
        <LocalizationItemDialog
          isOpen={locOpen}
          onClose={() => { setLocOpen(false); setLocItem(null) }}
          onSave={(updated) => setLocItem(updated)}
          item={locItem}
          controller={locController}
          parentObject={null}
          zIndex={100000}
          initialOverrides={{ [locController.primaryLanguage || "en"]: form.name }}
        />,
        document.body
      )}
    </Modal>
  )
}

// ─── Feature Dialog ──────────────────────────────────────────────────────────

function FeatureDialog({ feature, categories, onSave, onClose, onDelete }) {
  const [form, setForm] = useState({
    id: feature?.id || "",
    name: feature?.name || "",
    tooltip: feature?.tooltip || "",
    sort_order: feature?.sort_order ?? 0,
    category: feature?.category?._id || feature?.category || "",
    plan_values: feature?.plan_values || [],
    visible: feature?.visible !== false,
  })

  const [saving, setSaving] = useState(false)
  const locController = LocalizationController()
  const [locOpen, setLocOpen] = useState(false)
  const [locItem, setLocItem] = useState(null)
  const [locLoading, setLocLoading] = useState(false)
  const [locLangsLoaded, setLocLangsLoaded] = useState(false)
  const [locCurrentValue, setLocCurrentValue] = useState("")

  const openLocale = async (field, currentValue) => {
    if (!feature?._id) return
    const locKey = `ProductFeature-${field}-${feature._id}`
    setLocLoading(field)
    try {
      if (!locLangsLoaded) {
        const stats = await locController.getLanguageStatistics()
        if (stats?.languages) {
          locController.setLanguages(stats.languages)
          const primary = stats.languages.find((l) => l.primary)
          if (primary) locController.setPrimaryLanguage(primary.code)
          setLocLangsLoaded(true)
        }
      }
      let items = await locController.getLocalizationKey(locKey)
      if (!items || items.length === 0) {
        await locController.addLocalizationKey({
          key: locKey,
          type: "text",
          language: locController.primaryLanguage || "en",
          content: currentValue || "",
        })
        items = await locController.getLocalizationKey(locKey)
      }
      if (items?.[0]) {
        setLocCurrentValue(currentValue || "")
        setLocItem(items[0])
        setLocOpen(true)
      }
    } finally {
      setLocLoading(false)
    }
  }

  const handleSave = async () => {
    if (!form.id.trim() || !form.name.trim()) {
      showNotification({ message: "ID and Name are required", className: "bg-admin_error text-white" })
      return
    }
    setSaving(true)
    try {
      if (feature?._id) {
        await Promise.all([
          syncPrimaryLocale(locController, `ProductFeature-name-${feature._id}`, form.name),
          form.tooltip ? syncPrimaryLocale(locController, `ProductFeature-tooltip-${feature._id}`, form.tooltip) : Promise.resolve(),
        ])
      }
      onSave(form)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={feature?._id ? "Edit Feature" : "Add Feature"} variant="admin_compact_fit">
      <div className="p-6 space-y-4 w-[50vw] max-h-[80vh] overflow-y-auto">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-admin_text mb-1">ID *</label>
            <input
              value={form.id}
              onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
              className="w-full px-3 py-2 border border-admin_border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-admin_primary"
              placeholder="responses-per-month"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-admin_text mb-1">Name *</label>
            <div className="flex items-center gap-1">
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-admin_border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-admin_primary"
                placeholder="Responses / month"
              />
              {feature?._id && (
                <button
                  onClick={() => openLocale("name", form.name)}
                  disabled={locLoading === "name"}
                  className="p-1.5 text-admin_text_secondary hover:text-admin_primary transition disabled:opacity-50 flex-shrink-0"
                  title="Edit translations"
                >
                  <GlobeAltIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-admin_text mb-1">Sort Order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
              className="w-full px-3 py-2 border border-admin_border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-admin_primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-admin_text mb-1">Tooltip</label>
            <div className="flex items-center gap-1">
              <input
                value={form.tooltip}
                onChange={(e) => setForm((f) => ({ ...f, tooltip: e.target.value }))}
                className="w-full px-3 py-2 border border-admin_border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-admin_primary"
                placeholder="Optional tooltip text"
              />
              {feature?._id && (
                <button
                  onClick={() => openLocale("tooltip", form.tooltip)}
                  disabled={locLoading === "tooltip"}
                  className="p-1.5 text-admin_text_secondary hover:text-admin_primary transition disabled:opacity-50 flex-shrink-0"
                  title="Edit translations"
                >
                  <GlobeAltIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-admin_text mb-1">Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className="w-full px-3 py-2 border border-admin_border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-admin_primary bg-admin_paper text-admin_text"
          >
            <option value="">— No category —</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center justify-between py-1">
          <div>
            <label className="text-sm font-medium text-admin_text">Visible to users</label>
            <p className="text-xs text-admin_text_secondary mt-0.5">When off, this feature is hidden on the public pricing page</p>
          </div>
          <ToggleSwitch
            isOn={form.visible}
            onChange={(v) => setForm((f) => ({ ...f, visible: v }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-admin_text mb-2">Plan Values</label>
          <PlanValuesEditor
            value={form.plan_values}
            onChange={(v) => setForm((f) => ({ ...f, plan_values: v }))}
            parentObject={() => feature}
          />
        </div>
        <div className="flex items-center justify-between pt-2">
          {feature?._id && onDelete ? (
            <button
              onClick={onDelete}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-admin_error border border-admin_error/40 rounded-md hover:border-admin_error hover:bg-admin_error/5 transition"
            >
              <TrashIcon className="w-3.5 h-3.5" />
              Delete
            </button>
          ) : <span />}
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>Save</Button>
          </div>
        </div>
      </div>
      {locOpen && locItem && createPortal(
        <LocalizationItemDialog
          isOpen={locOpen}
          onClose={() => { setLocOpen(false); setLocItem(null) }}
          onSave={(updated) => setLocItem(updated)}
          item={locItem}
          controller={locController}
          parentObject={null}
          zIndex={100000}
          initialOverrides={{ [locController.primaryLanguage || "en"]: locCurrentValue }}
        />,
        document.body
      )}
    </Modal>
  )
}

// ─── Cell with portal editor ─────────────────────────────────────────────────

function CellWithEditor({ feature, plan, isEditing, getValue, getRawEntry, setEditingCell, applyInlineCellEdit }) {
  const cellRef = useRef(null)
  const entry = getRawEntry(feature, plan)
  return (
    <td
      ref={cellRef}
      className="px-4 py-2.5 text-center cursor-pointer"
      onClick={() => !isEditing && setEditingCell({ featureId: feature._id, planId: plan._id })}
    >
      <ValueBadge value={getValue(feature, plan)} />
      {isEditing && (
        <InlineCellEditor
          entry={entry}
          anchorRef={cellRef}
          featureId={feature._id}
          planId={plan._id}
          pfvId={entry?._id}
          onApply={(p, v, cf) => { setEditingCell(null); applyInlineCellEdit(feature, plan._id, p, v, cf) }}
          onClose={() => setEditingCell(null)}
        />
      )}
    </td>
  )
}

// ─── Main Editor ─────────────────────────────────────────────────────────────

export default function FeatureMatrixEditor() {
  const { saveContent, deleteContent, getContent } = CMSController()
  const [refreshKey, setRefreshKey] = useState(0)
  const [editCategory, setEditCategory] = useState(null)
  const [editFeature, setEditFeature] = useState(null)
  const [addingFeatureToCategory, setAddingFeatureToCategory] = useState(null)
  const [allPlans, setAllPlans] = useState(null)
  // matrix: { _id, id, plans: [planId, ...] } — source of truth for active plan columns
  const [matrix, setMatrix] = useState(null)
  const [showAddPlan, setShowAddPlan] = useState(false)
  const [categories, setCategories] = useState(null)
  const [features, setFeatures] = useState(null)
  const [search, setSearch] = useState("")
  const [editingCell, setEditingCell] = useState(null) // { featureId, planId }
  const [collapsedCategories, setCollapsedCategories] = useState(new Set())
  const collapsedInitialized = useRef(false)
  const [localCategories, setLocalCategories] = useState([])
  const [localFeatures, setLocalFeatures] = useState([])
  const localCategoriesRef = useRef([])
  const localFeaturesRef = useRef([])
  const dragCatRef = useRef(null)
  const dragFeatRef = useRef(null)
  const suppressFeaturesSync = useRef(false)

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), [])

  const fetchCategories = useCallback(() => {
    getContent({ match: { "content.type": "FeatureCategory" }, sort: { "content.sort_order": 1 }, itemsPerPage: 100, expand: false })
      .then((r) => r?.data && setCategories(r.data.map((d) => ({ _id: d._id, ...d.content, localization: d.localization || [] }))))
  }, [])

  useEffect(() => {
    if (!categories) return
    const copy = [...categories]
    localCategoriesRef.current = copy
    setLocalCategories(copy)
    if (collapsedInitialized.current) return
    collapsedInitialized.current = true
    const saved = LocalUserPreferences.get(COLLAPSED_PREF_KEY)
    if (Array.isArray(saved)) {
      setCollapsedCategories(new Set(saved))
    } else {
      setCollapsedCategories(new Set(copy.map((c) => c._id)))
    }
  }, [categories])
  useEffect(() => {
    if (!features) return
    if (suppressFeaturesSync.current) return
    const copy = [...features]
    localFeaturesRef.current = copy
    setLocalFeatures(copy)
  }, [features])

  // ── Category drag ────────────────────────────────────────────────────────
  const handleCatDragStart = (index) => { dragCatRef.current = index }
  const handleCatDragOver = (e, index) => {
    e.preventDefault()
    const from = dragCatRef.current
    if (from === null || from === undefined || from === index) return
    const next = [...localCategoriesRef.current]
    const [item] = next.splice(from, 1)
    next.splice(index, 0, item)
    localCategoriesRef.current = next
    setLocalCategories(next)
    dragCatRef.current = index
  }
  const handleCatDragEnd = async () => {
    dragCatRef.current = null
    await Promise.all(
      localCategoriesRef.current.map(({ _id, ...cat }, i) =>
        saveContent({ payload: { _id, content: { ...cat, sort_order: i } } }, false)
      )
    )
  }

  // ── Feature drag ─────────────────────────────────────────────────────────
  const getCatId = (f) => (typeof f.category === "string" ? f.category : f.category?._id)
  const handleFeatDragStart = (catId, index) => { dragFeatRef.current = { catId, index } }
  const handleFeatDragOver = (e, catId, index) => {
    e.preventDefault()
    if (!dragFeatRef.current || dragFeatRef.current.catId !== catId) return
    const from = dragFeatRef.current.index
    if (from === index) return
    const current = localFeaturesRef.current
    const catFeatures = current.filter((f) => getCatId(f) === catId)
    const others = current.filter((f) => getCatId(f) !== catId)
    const [item] = catFeatures.splice(from, 1)
    catFeatures.splice(index, 0, item)
    const next = [...others, ...catFeatures]
    localFeaturesRef.current = next
    setLocalFeatures(next)
    dragFeatRef.current = { catId, index }
  }
  const handleFeatDragEnd = async () => {
    const info = dragFeatRef.current
    dragFeatRef.current = null
    if (!info) return
    const catFeatures = localFeaturesRef.current.filter((f) => getCatId(f) === info.catId)
    await Promise.all(
      catFeatures.map(({ _id, ...feat }, i) => {
        const pvIds = (feat.plan_values || []).map((pv) => (typeof pv === "string" ? pv : pv?._id)).filter(Boolean)
        const { content, localization } = normalizeFeatForSave(feat, _id, pvIds)
        return saveContent({ payload: { _id, content: { ...content, sort_order: i }, localization } }, false)
      })
    )
  }

  // ── Data loading ─────────────────────────────────────────────────────────
  useEffect(() => {
    getContent({ match: { "content.type": "ProductPlan" }, sort: { "content.sort_order": 1 }, itemsPerPage: 50, expand: false })
      .then((r) => {
        if (!r?.data) return
        setAllPlans(r.data.map((d) => ({ _id: d._id, ...d.content })))
      })
    getContent({ match: { "content.type": "ProductFeatureMatrix", "content.id": "main" }, itemsPerPage: 1, expand: false })
      .then((r) => {
        if (!r?.data?.[0]) return
        const d = r.data[0]
        setMatrix({
          _id: d._id,
          ...d.content,
          plans: (d.content.plans || []).map((p) => resolvePlanId(p)),
        })
      })
    getContent({ match: { "content.type": "FeatureCategory" }, sort: { "content.sort_order": 1 }, itemsPerPage: 100, expand: false })
      .then((r) => r?.data && setCategories(r.data.map((d) => ({ _id: d._id, ...d.content, localization: d.localization || [] }))))
    Promise.all([
      getContent({ match: { "content.type": "ProductFeature" }, sort: { "content.sort_order": 1 }, itemsPerPage: 500, expand: false }),
      loadPfvMap(getContent),
    ]).then(([featRes, pfvMap]) => {
      if (!featRes?.data) return
      const flat = featRes.data.map((d) => ({ _id: d._id, ...d.content, localization: d.localization || [] }))
      const hydrated = hydrateFeatures(flat, pfvMap)
      setFeatures(hydrated)
    })
  }, [refreshKey])

  // ── Matrix plan helpers ───────────────────────────────────────────────────
  const saveMatrix = async (updatedMatrix) => {
    const { _id, ...content } = updatedMatrix
    await saveContent({ payload: { _id, content } }, false)
  }

  const handleAddPlan = async (planId) => {
    const next = { ...matrix, plans: [...(matrix.plans || []), planId] }
    setMatrix(next)
    await saveMatrix(next)
    setShowAddPlan(false)
  }

  const handleRemovePlan = async (plan) => {
    const ok = await showPromptDialogAsync({
      title: "Remove Plan",
      message: `Remove "${plan.name}" from the matrix? This will also delete its values from all features.`,
    })
    if (!ok) return
    // Clean up plan_values from all features that reference this plan.
    // Delete the standalone PlanFeatureValue docs whose `plan` matches.
    const toUpdate = (features || []).filter((f) =>
      f.plan_values?.some((pv) => resolvePlanId(pv.plan) === plan._id)
    )
    await Promise.all(
      toUpdate.map(async ({ _id, ...f }) => {
        const keep = []
        const drop = []
        ;(f.plan_values || []).forEach((pv) => {
          (resolvePlanId(pv.plan) === plan._id ? drop : keep).push(pv)
        })
        // Delete dropped PFV cmscontents
        await Promise.all(drop.map((pv) => deletePlanFeatureValue(deleteContent, pv._id)))
        const keepIds = keep.map((pv) => (typeof pv === "string" ? pv : pv._id)).filter(Boolean)
        const { content, localization } = normalizeFeatForSave({ ...f, plan_values: keep }, _id, keepIds)
        return saveContent({ payload: { _id, content, localization } }, false)
      })
    )
    // Update matrix instance
    const next = { ...matrix, plans: (matrix.plans || []).filter((id) => id !== plan._id) }
    setMatrix(next)
    await saveMatrix(next)
    refresh()
  }

  // Group features by category (use local ordered state for drag-reorder)
  const q = search.trim().toLowerCase()
  const grouped = localCategories
    .map((cat) => {
      const catFeatures = localFeatures.filter((f) => getCatId(f) === cat._id)
      if (!q) return { category: cat, features: catFeatures }
      const catMatch = cat.name?.toLowerCase().includes(q)
      const featureMatches = (f) =>
        f.name?.toLowerCase().includes(q) ||
        f.plan_values?.some((pv) => pv.param?.toLowerCase().includes(q))
      return {
        category: cat,
        features: catMatch ? catFeatures : catFeatures.filter(featureMatches),
        _catMatch: catMatch,
      }
    })
    .filter((g) => g._catMatch || g.features.length > 0)

  const categorisedIds = new Set(localCategories.map((c) => c._id))
  const uncategorised = localFeatures.filter((f) => {
    const fCatId = getCatId(f)
    if (fCatId && categorisedIds.has(fCatId)) return false
    if (!q) return true
    return f.name?.toLowerCase().includes(q) ||
      f.plan_values?.some((pv) => pv.param?.toLowerCase().includes(q))
  })
  if (uncategorised.length > 0) {
    grouped.push({ category: { name: "Uncategorised" }, features: uncategorised })
  }

  // ── Save handlers ────────────────────────────────────────────────────────

  const handleSaveCategory = async (form) => {
    const _id = editCategory?._id
    const existingLoc = Array.isArray(editCategory?.localization) ? editCategory.localization : []
    const localization = [...existingLoc]
    if (_id && !localization.some((l) => l.field === "name")) {
      localization.push({ field: "name", localizationKey: `FeatureCategory-name-${_id}` })
    }
    await saveContent({
      payload: {
        ...(_id ? { _id } : {}),
        content: { type: "FeatureCategory", ...form },
        ...(localization.length ? { localization } : {}),
      },
    })
    setEditCategory(null)
    fetchCategories()
  }

  const handleSaveFeature = async (form) => {
    const base = editFeature?._id ? editFeature : addingFeatureToCategory ? { category: addingFeatureToCategory } : {}
    const previousIds = (base.plan_values || []).map((pv) => (typeof pv === "string" ? pv : pv?._id)).filter(Boolean)
    // Persist each plan_value as a PlanFeatureValue cmscontent first.
    const persistedIds = await savePlanValues(
      { saveContent, deleteContent },
      form.plan_values || [],
      previousIds,
    )
    const { content, localization } = normalizeFeatForSave({
      type: "ProductFeature",
      localization: base.localization,
      ...form,
      category: form.category || base.category,
    }, base._id, persistedIds)
    // Optimistic update — keep hydrated objects in state for the table
    if (base._id) {
      const updated = { ...base, ...content, plan_values: form.plan_values || [] }
      setFeatures((prev) => prev.map((f) => f._id === base._id ? updated : f))
      setLocalFeatures((prev) => prev.map((f) => f._id === base._id ? updated : f))
      localFeaturesRef.current = localFeaturesRef.current.map((f) => f._id === base._id ? updated : f)
    }
    setEditFeature(null)
    setAddingFeatureToCategory(null)
    suppressFeaturesSync.current = true
    await saveContent({ payload: { ...(base._id ? { _id: base._id } : {}), content, localization } })
    suppressFeaturesSync.current = false
    if (!base._id) refresh()
  }

  const handleDeleteCategory = async (cat) => {
    const ok = await showPromptDialogAsync({ title: "Delete Category", message: `Delete "${cat.name}"?` })
    if (!ok) return
    await deleteContent(cat._id)
    fetchCategories()
  }

  const applyInlineCellEdit = async (feature, planId, newParam, newValue, customFields = []) => {
    const params = Object.fromEntries(customFields.map((f) => [f.key, f.value]))
    const existing = feature.plan_values?.find((pv) => resolvePlanId(pv.plan) === planId)
    const newPlanValues = existing
      ? feature.plan_values.map((pv) => {
          if (resolvePlanId(pv.plan) !== planId) return pv
          return { _id: pv._id, plan: pv.plan, param: newParam, value: newValue, params, localization: pv.localization }
        })
      : [...(feature.plan_values || []), { plan: planId, param: newParam, value: newValue, params }]
    const { _id, ...feat } = feature
    const previousIds = (feature.plan_values || []).map((pv) => (typeof pv === "string" ? pv : pv?._id)).filter(Boolean)
    const persistedIds = await savePlanValues(
      { saveContent, deleteContent },
      newPlanValues,
      previousIds,
    )
    // Patch in-memory pfvs with their new ids (so subsequent edits reuse the same doc)
    const persistedPlanValues = newPlanValues.map((pv, i) => ({ ...pv, _id: pv._id || persistedIds[i] }))
    const updated = { ...feature, plan_values: persistedPlanValues }
    setFeatures((prev) => prev.map((f) => f._id === feature._id ? updated : f))
    setLocalFeatures((prev) => prev.map((f) => f._id === feature._id ? updated : f))
    localFeaturesRef.current = localFeaturesRef.current.map((f) => f._id === feature._id ? updated : f)
    suppressFeaturesSync.current = true
    const { content, localization } = normalizeFeatForSave({ ...feat, plan_values: persistedPlanValues }, _id, persistedIds)
    await saveContent({ payload: { _id, content, localization } }, false)
    suppressFeaturesSync.current = false
  }

  const handleDeleteFeature = async (feature) => {
    const ok = await showPromptDialogAsync({ title: "Delete Feature", message: `Delete "${feature.name}"?` })
    if (!ok) return
    setFeatures((prev) => prev.filter((f) => f._id !== feature._id))
    setLocalFeatures((prev) => prev.filter((f) => f._id !== feature._id))
    localFeaturesRef.current = localFeaturesRef.current.filter((f) => f._id !== feature._id)
    await deleteContent(feature._id)
  }

  const toggleCategory = (catId) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      next.has(catId) ? next.delete(catId) : next.add(catId)
      LocalUserPreferences.set(COLLAPSED_PREF_KEY, [...next])
      return next
    })
  }

  if (!allPlans || !matrix || !categories || !features) {
    return <div className="p-8 text-admin_text_secondary">Loading...</div>
  }

  const activePlanIds = matrix.plans || []
  const activePlans = activePlanIds
    .map((id) => allPlans.find((p) => p._id === id))
    .filter(Boolean)
  const inactivePlans = allPlans.filter((p) => !activePlanIds.includes(p._id))

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="admin_h2">Feature Matrix</Typography>
        <div className="flex items-center gap-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
            placeholder="Search features..."
            showClearButton
            variant="admin"
            className="w-[200px]"
          />
          <Button variant="secondary" onClick={() => setEditCategory({})}>
            + Add Category
          </Button>
          <Button variant="primary" onClick={() => { setAddingFeatureToCategory(null); setEditFeature({}) }}>
            + Add Feature
          </Button>
        </div>
      </div>

      <div className="overflow-auto rounded-2xl border border-admin_border bg-admin_paper max-h-[calc(100vh-220px)]">
        <table className="w-full text-left">
          <thead className="sticky top-0 z-20 bg-admin_paper border-b border-admin_border">
            <tr>
              <th className="sticky left-0 z-30 bg-admin_paper px-4 py-3 text-sm font-semibold text-admin_text_secondary w-[220px]">Feature</th>
              {activePlans.map((plan) => (
                <th key={plan._id} className="px-4 py-3 text-sm font-semibold text-admin_text text-center min-w-[120px]">
                  <div className="flex items-center justify-center gap-2">
                    <span>{plan.name}</span>
                    <button
                      onClick={() => handleRemovePlan(plan)}
                      className="text-admin_error/50 hover:text-admin_error"
                      title="Remove plan"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 min-w-[100px] text-center relative">
                {inactivePlans.length > 0 && (
                  <div className="relative inline-block">
                    <button
                      onClick={() => setShowAddPlan((v) => !v)}
                      className="text-xs font-medium text-admin_primary hover:text-admin_primary/80 whitespace-nowrap"
                    >
                      + Add plan
                    </button>
                    {showAddPlan && (
                      <div className="absolute right-0 top-full mt-1 z-50 bg-admin_paper border border-admin_border rounded-lg shadow-lg min-w-[140px]">
                        {inactivePlans.map((p) => (
                          <button
                            key={p._id}
                            onClick={() => handleAddPlan(p._id)}
                            className="w-full text-left px-4 py-2 text-sm text-admin_text hover:bg-admin_neutral_1"
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((group, gi) => (
              group.features.length > 0 || group.category._id ? (
                <>
                  {/* Category row */}
                  <tr
                    key={`cat-${group.category._id || gi}`}
                    draggable={!!group.category._id}
                    onDragStart={() => group.category._id && handleCatDragStart(gi)}
                    onDragOver={(e) => group.category._id && handleCatDragOver(e, gi)}
                    onDragEnd={handleCatDragEnd}
                    className="group bg-admin_canvas cursor-pointer select-none border-t border-admin_border/50"
                    onClick={() => group.category._id && toggleCategory(group.category._id)}
                  >
                    <td colSpan={activePlans.length + 2} className="px-4 py-2">
                      <div className="flex items-center gap-3">
                        {group.category._id && (
                          <Bars3Icon className="w-4 h-4 text-admin_text_secondary/40 cursor-move flex-shrink-0" onClick={(e) => e.stopPropagation()} />
                        )}
                        <span className={`text-sm font-bold tracking-wide ${group.category.visible === false ? "text-admin_text_secondary/40 line-through" : "text-admin_text"}`}>
                          {group.category.name}
                        </span>
                        {group.category.visible === false && (
                          <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-admin_text_secondary/10 text-admin_text_secondary/60">
                            <EyeSlashIcon className="w-3 h-3" />
                            hidden
                          </span>
                        )}
                        {group.category._id && (
                          <div className="flex items-center gap-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => { e.stopPropagation(); setAddingFeatureToCategory(group.category._id); setEditFeature({}) }}
                              className="text-xs text-admin_primary hover:underline"
                            >
                              + Add feature
                            </button>
                            <button
                              onClick={() => setEditCategory(group.category)}
                              className="text-xs text-admin_primary hover:underline"
                            >
                              Edit group
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(group.category)}
                              className="text-admin_error hover:text-admin_error/70"
                              title="Delete group"
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Feature rows */}
                  {(q || !collapsedCategories.has(group.category._id)) && group.features.map((feature, fi) => (
                    <tr
                      key={feature._id || fi}
                      draggable
                      onDragStart={() => handleFeatDragStart(group.category._id, fi)}
                      onDragOver={(e) => handleFeatDragOver(e, group.category._id, fi)}
                      onDragEnd={handleFeatDragEnd}
                      className={`group ${fi % 2 === 0 ? "bg-admin_paper" : "bg-admin_canvas"} hover:bg-admin_neutral_1 transition-colors border-b border-admin_border/40`}
                    >
                      <td className={`sticky left-0 z-10 px-4 py-2.5 ${fi % 2 === 0 ? "bg-admin_paper" : "bg-admin_canvas"} group-hover:bg-admin_neutral_1 transition-colors`}>
                        <div className="flex items-center gap-2">
                          <Bars3Icon className="w-4 h-4 text-admin_text_secondary/30 cursor-move flex-shrink-0" />
                          <button
                            onClick={() => setEditFeature(feature)}
                            className={`text-sm hover:text-admin_primary hover:underline text-left ${feature.visible === false ? "text-admin_text_secondary/50 line-through" : "text-admin_text"}`}
                          >
                            {feature.name}
                          </button>
                          {feature.tooltip && (
                            <span className="text-admin_text_secondary cursor-help text-xs" title={feature.tooltip}>ⓘ</span>
                          )}
                          {feature.visible === false && (
                            <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-admin_text_secondary/10 text-admin_text_secondary/60">
                              <EyeSlashIcon className="w-3 h-3" />
                              hidden
                            </span>
                          )}
                        </div>
                      </td>
                      {activePlans.map((plan) => {
                        const isEditing = editingCell?.featureId === feature._id && editingCell?.planId === plan._id
                        return (
                          <CellWithEditor
                            key={plan._id}
                            feature={feature}
                            plan={plan}
                            isEditing={isEditing}
                            getValue={getValue}
                            getRawEntry={getRawEntry}
                            setEditingCell={setEditingCell}
                            applyInlineCellEdit={applyInlineCellEdit}
                          />
                        )
                      })}
                      <td />
                    </tr>
                  ))}
                </>
              ) : null
            ))}
          </tbody>
        </table>
      </div>

      {/* Category dialog */}
      {editCategory !== null && (
        <CategoryDialog
          category={editCategory}
          onSave={handleSaveCategory}
          onClose={() => setEditCategory(null)}
        />
      )}

      {/* Feature dialog */}
      {(editFeature !== null || addingFeatureToCategory !== null) && (
        <FeatureDialog
          feature={editFeature}
          categories={categories || []}
          onSave={handleSaveFeature}
          onClose={() => { setEditFeature(null); setAddingFeatureToCategory(null) }}
          onDelete={editFeature?._id ? async () => {
            setEditFeature(null)
            setAddingFeatureToCategory(null)
            await handleDeleteFeature(editFeature)
          } : undefined}
        />
      )}
    </div>
  )
}
